import * as _ from "lodash";
import * as React from "react";
import { Menu } from "antd";
import { ClickParam } from "antd/lib/menu";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { ClubHub, XSocialSymbols, IClubHubService } from "@xbox/social-core";
import { XSocialContainer } from "../../inversify.config";

import * as cmtStyles from "./ClubMenuTabs.Styles";
import { ClubMenuMode } from "./ClubMenu";
import { CurrentUserContext, withCurrentUserContext } from "../CurrentUserContext";
import { ClubSelectionContext, withClubSelectionContext } from "../ClubSelectionContext";
import { CreateOfficialClubDialog } from "../dialogs/CreateOfficialClubDialog";
import { telemetryService } from "../../index";
import { Glyphs } from "../../common/Generic.Icons";
import { Localize } from "../../common/translation/Localize";
import { Icon } from "../../common/GenericElements";
import { ClubpicListItem } from "../../common/generic/ClubpicListItem";
import { IsDevMode } from "../../common/GenericChecks";
import { IsAuthorizedForOfficialClubCreation } from "../../common/UserAuthorization";
import Util from "../../common/Util";

const SubMenu = Menu.SubMenu;

interface ClubMenuTabsStateProps {
}

interface ClubMenuTabsDispatchProps {
    handleNavigation: (route: string) => void;
}

export interface ClubMenuTabsOtherProps {
    mode: ClubMenuMode;
    isOpen: boolean;
    allClubs: ClubHub.Club[];
    currentPath?: string;
    toggleMenuCommand: () => void;
}

type ClubMenuTabsProps = ClubMenuTabsStateProps & ClubMenuTabsDispatchProps & ClubMenuTabsOtherProps & CurrentUserContext & ClubSelectionContext;

export interface ClubMenuTabState {
    managedClubs: ClubHub.Club[];
    officialClubs: ClubHub.Club[];
    socialClubs: ClubHub.Club[];
    showCreateOfficialClubDialog: boolean;
}

class ClubMenuTabs extends React.Component<ClubMenuTabsProps, ClubMenuTabState> {
    static PRIVACY: string = `/privacy`;
    private clubHubService: IClubHubService;

    constructor(props: ClubMenuTabsProps) {
        super(props);

        this.clubHubService = XSocialContainer.get<IClubHubService>(XSocialSymbols.IClubHubService);

        this.state = {
            managedClubs: [],
            officialClubs: [],
            socialClubs: [],
            showCreateOfficialClubDialog: false
        };
    }

    componentDidMount() {
        if (this.props.allClubs) {
            this.updateClubList();
        }
        window.addEventListener("keydown", this.handleKeyDown);
    }

    componentWillUnmount() {
        window.removeEventListener("keydown", this.handleKeyDown);
    }

    componentDidUpdate(prevProps: ClubMenuTabsProps) {
        if (prevProps.allClubs !== this.props.allClubs) {
            this.updateClubList();
        }
    }

    updateClubList = () => {
        let allData = this.props.allClubs.slice();
        // separate to managed and others:
        let managed = _.remove(allData, (club: ClubHub.Club) => club && club.settings!.viewerRoles.roles.indexOf(ClubHub.ClubRoles.Moderator) > -1);
        // separate left-overs to official and others (social):
        let official = _.remove(allData, (club: ClubHub.Club) => club && club.details.isOfficialClub);
        const mainMenu = document.querySelectorAll(".ant-menu-submenu-title") as NodeListOf<Element>;
        mainMenu.forEach((menu) => {
            menu.setAttribute("tabindex", "0");
            menu.setAttribute("role", "button");
            menu.setAttribute("type", "button");
        });
        this.setState({
            managedClubs: managed,
            officialClubs: official,
            socialClubs: allData,
        });
    }

    onClick = (param: ClickParam) => {
        // Allows passing the open menu function down to the child members of the menu
        // and avoids the navigation behavior of the router
        if (param.key === `AlternateFunction` && this.props.toggleMenuCommand) {
            this.props.toggleMenuCommand();
            return;
        }

        // Handle official club tools dialog
        if (param.key === "manage-official-clubs") {
            this.setState({ showCreateOfficialClubDialog: true });
            return;
        }

        if (param.key === this.props.currentPath) {
            // avoid routing on click if already at that route location
            return;
        }

        if (param.keyPath.length > 2 || param.key === "ignore") {
            // this is used to ignore already handled events.
            return;
        }

        let pageRoute = param.key;
        telemetryService.trackEvent("NavigationMenuItemClicked", { "PageName": `/app/${pageRoute}` });
        if (isNaN(parseInt(pageRoute, 10))) {
            this.props.handleNavigation(`/app/${pageRoute}`);
        } else {
            this.props.handleNavigation(`/app/${pageRoute}/discussion/feed`);
        }
    }

    getClubsItems = (clubList: ClubHub.Club[], key: string) => {
        return (
            <SubMenu key={key} title={Localize(`clubMenu.${key}`)}>
                {clubList.map((item: ClubHub.Club) => {
                    return (
                        <cmtStyles.ClubMenuItem key={item.details.id} tabIndex={0}>
                            {this.props.isOpen && item.details.isOfficialClub &&
                                <Icon className="club-badge" type={Glyphs.VerifiedGameClubBackplate}/>
                            }
                            <ClubpicListItem
                                displayPicUrl={Util.ensureSSLImage(item.details.profile.displayImageUrl.value)}
                                displayPicName={this.props.isOpen ? item.details.profile.name.value : ""}
                            />
                        </cmtStyles.ClubMenuItem>
                    );
                })}
            </SubMenu>
        );
    }

    handleKeyDown = (event: KeyboardEvent): void => {
        const activeElement = document.activeElement as HTMLElement;
        if (event.key === "Enter" || activeElement.classList.contains("ant-menu-submenu")) {
                activeElement.click();
        }
    }

    handleCloseCreateOfficialClubDialog = () => {
        this.setState({ showCreateOfficialClubDialog: false });
    }

    handleValidateTitleId = async (titleId: string): Promise<any> => {
        const response = await this.clubHubService.lookupTitleId(titleId);
        return response;
    }

    handleValidateGamertag = async (gamertag: string): Promise<{xuid: string, gamertag: string, displayPic: string}> => {
        const response = await this.clubHubService.lookUpXuidFromGamertag(gamertag);
        return response.data;
    }

    handleValidateClubId = async (clubId: string): Promise<{
        success: boolean;
        result?: {
            clubId: string;
            clubName: string;
            ownerGamertag: string;
            clubPicture?: string;
            description?: string;
            ownerDisplayPicRaw?: string;
        };
        error?: string;
    }> => {
        try {
            // Call service to fetch club details
            const response = await this.clubHubService.getClubDetails(clubId);
            const details = response.data.club.details;
            return {
                success: true,
                result: {
                    clubId: clubId,
                    clubName: details.profile.name.value,
                    ownerGamertag: `${details.owner.gamertag} (XUID: ${details.owner.xuid})`,
                    clubPicture: Util.ensureSSLImage(details.profile.displayImageUrl.value),
                    // Add new fields:
                    description: details.profile.description?.value,
                    ownerDisplayPicRaw: details.owner.displayPicRaw ? Util.ensureSSLImage(details.owner.displayPicRaw) : undefined
                }
            };
        } catch (error) {
            return {
                success: false,
                error: `Failed to validate club: ${error instanceof Error ? error.message : "Unknown error"}`
            };
        }
    }

    handleCreateClub = async (clubName: string): Promise<{success: boolean, clubId?: string, error?: string}> => {
        try {
            // Create the official club
            const clubResult = await this.clubHubService.createOfficialClub(clubName);
            return {
                success: true,
                clubId: clubResult.clubId
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to create official club";
            return {
                success: false,
                error: errorMessage
            };
        }
    }

    handleTransferOwnership = async (clubId: string, targetXuid: string): Promise<{success: boolean, error?: string}> => {
        try {
            // Add moderator to the club
            await this.clubHubService.addModeratorToOfficialClub(targetXuid, clubId);

            // Transfer ownership to the user
            await this.clubHubService.transferOwnership(clubId, targetXuid);

            return {
                success: true
            };
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : "Failed to transfer ownership";
            return {
                success: false,
                error: errorMessage
            };
        }
    }

    getDefaultItems = (navigationKey: string, glyph: Glyphs, displayKey?: string) => {
        let translationKey = displayKey ? displayKey : navigationKey;
        return (
            <Menu.Item key={navigationKey} tabIndex={0}>
                <cmtStyles.MenuItemIcon><Icon type={glyph} /></cmtStyles.MenuItemIcon>
                <cmtStyles.MenuItemLabel>{Localize(`clubMenu.${translationKey}`)}</cmtStyles.MenuItemLabel>
            </Menu.Item>
        );
    }

    render() {
        return (
            <Menu
                mode={this.props.mode}
                onClick={this.onClick}
                style={{ height: "100%" }}
                selectedKeys={this.props.currentPath ? [this.props.currentPath] : []}
                defaultOpenKeys={[`managed`, `official`, `social`]}
                className="clubMenu"
            >
                {this.getDefaultItems("home/all", Glyphs.Home, "home")}
                {IsDevMode() && this.getDefaultItems("people", Glyphs.PlayerAdd)}
                {IsDevMode() && this.getDefaultItems("discovery", Glyphs.Internet)}
                {IsAuthorizedForOfficialClubCreation(this.props.user) && this.getDefaultItems("manage-official-clubs", Glyphs.Settings, "manageOfficialClubs")}

                {this.getClubsItems(this.state.managedClubs, "managed")}
                {this.getClubsItems(this.state.officialClubs, "official")}
                {this.getClubsItems(this.state.socialClubs, "social")}

                {this.state.showCreateOfficialClubDialog && (
                    <CreateOfficialClubDialog
                        onClose={this.handleCloseCreateOfficialClubDialog}
                        onCreateClub={this.handleCreateClub}
                        onTransferOwnership={this.handleTransferOwnership}
                        onValidateTitleId={this.handleValidateTitleId}
                        onValidateGamertag={this.handleValidateGamertag}
                        onValidateClubId={this.handleValidateClubId}
                    />
                )}
            </Menu>
        );
    }
}

const mapStateToProps = (state: any): ClubMenuTabsStateProps => {
    return {};
};

const mapDispatchToProps = (dispatch: any): ClubMenuTabsDispatchProps => {
    return {
        handleNavigation: (route: string) => dispatch(push(route))
    };
};

export default withClubSelectionContext(withCurrentUserContext(connect(
    mapStateToProps,
    mapDispatchToProps,
)(ClubMenuTabs)));
